import React, { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { TITLES, autoStatus, createEvent } from '../../services/tripleCrown';
import { CAR_CLASSES, LAP_OPTIONS, TYRES, getVariablesByTitle } from '../../data/tracks';
import TrackMultiSelect from './TrackMultiSelect';
import TrackSettings, { cleanTrackSettings } from './TrackSettings';
import CarField from './CarField';
import { barbadosToUTC } from '../../utils/championshipDates';
import useRules from '../../hooks/useRules';

/* ══════════════════════════════════════
   ADD ROUND MODAL
   ══════════════════════════════════════ */
export default function AddRoundModal({ championshipId, existingCount, onClose, onSaved }) {
  const availableRules = useRules();
  const [round, setRound] = useState({
    titleId: TITLES[0].id, scoringMode: 'time', name: '', description: '',
    ruleId: null, startDate: '', endDate: '', tracks: [], laps: null, carClass: null, tyre: null, trackSettings: [], carName: '', carImage: '', variables: [],
    sortOrder: '', awardsPoints: true,
  });
  const [saving, setSaving] = useState(false);

  const variables = getVariablesByTitle(round.titleId);
  const set = (k, v) => setRound(r => ({ ...r, [k]: v }));

  const toggleVariable = (varId) => {
    const current = round.variables || [];
    if (current.includes(varId)) set('variables', current.filter(v => v !== varId));
    else set('variables', [...current, varId]);
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const titleObj = TITLES.find(t => t.id === round.titleId);
      const roundStatus = autoStatus(round.startDate, round.endDate);
      await createEvent({
        championship: championshipId,
        titleId: round.titleId,
        titleName: titleObj?.name || round.titleId,
        phase: round.scoringMode === 'time' ? 1 : 2,
        phaseName: round.name?.trim() || `Round ${existingCount + 1}`,
        description: round.description?.trim() || '',
        ruleId: round.ruleId || null,
        scoringMode: round.scoringMode,
        tracks: round.tracks || [],
        track: (round.tracks || []).join(', '),
        laps: round.laps || null,
        carClass: round.titleId === 'granTurismo7' ? (round.carClass || null) : null,
        tyre: round.titleId === 'granTurismo7' ? (round.tyre || null) : null,
        trackSettings: cleanTrackSettings(round.tracks, round.trackSettings, round.titleId),
        carName: (round.carName || '').trim() || null,
        carImage: round.carImage || null,
        variables: round.variables || [],
        startDate: round.startDate ? Timestamp.fromDate(barbadosToUTC(round.startDate)) : null,
        endDate: round.endDate ? Timestamp.fromDate(barbadosToUTC(round.endDate)) : null,
        scheduledDate: round.startDate ? Timestamp.fromDate(barbadosToUTC(round.startDate)) : null,
        status: roundStatus,
        livestreamUrl: '',
        sortOrder: round.sortOrder !== '' ? parseInt(round.sortOrder) : null,
        awardsPoints: round.awardsPoints,
      });
      onSaved();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2 className="modal-title">Add Round to Championship</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Game Title</label>
            <select className="form-select" value={round.titleId} onChange={e => {
              setRound(r => ({ ...r, titleId: e.target.value, tracks: [], variables: [] }));
            }}>
              {TITLES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Scoring Mode</label>
            <select className="form-select" value={round.scoringMode} onChange={e => set('scoringMode', e.target.value)}>
              <option value="time">Time-Based</option>
              <option value="position">Position-Based</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={round.awardsPoints} onChange={e => set('awardsPoints', e.target.checked)} />
            <span style={{ fontWeight: 600 }}>Awards Points</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {round.awardsPoints ? '— Points calculated from positions' : '— Position only, no points awarded'}
            </span>
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">Round Name</label>
          <input className="form-input" value={round.name} onChange={e => set('name', e.target.value)}
            placeholder={`e.g. Round ${existingCount + 1}, Semi-Final`} autoFocus />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={3} value={round.description} onChange={e => set('description', e.target.value)}
            placeholder="Brief description" style={{ resize: 'vertical' }} />
        </div>

        <div className="form-group">
          <label className="form-label">Attached Rule Set</label>
          <select className="form-select" value={round.ruleId || ''} onChange={e => set('ruleId', e.target.value || null)}>
            <option value="">None</option>
            {availableRules.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Start Date & Time</label>
            <input className="form-input" type="datetime-local" value={round.startDate}
              onChange={e => set('startDate', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date & Time</label>
            <input className="form-input" type="datetime-local" value={round.endDate}
              onChange={e => set('endDate', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Tracks</label>
          <TrackMultiSelect titleId={round.titleId} selected={round.tracks || []} onChange={tracks => set('tracks', tracks)} />
          <TrackSettings titleId={round.titleId} tracks={round.tracks || []} settings={round.trackSettings}
            onChange={v => set('trackSettings', v)} defaultLaps={round.laps} defaultCarClass={round.carClass} defaultTyre={round.tyre} />
        </div>

        <CarField name={round.carName} image={round.carImage} uploadKey="add-round"
          onName={v => set('carName', v)} onImage={v => set('carImage', v)} />

        <div className="form-row">
          <div className="form-group" style={{ maxWidth: 120 }}>
            <label className="form-label">Laps</label>
            <select className="form-select" value={round.laps || ''} onChange={e => set('laps', e.target.value ? parseInt(e.target.value) : null)}>
              <option value="">N/A</option>
              {LAP_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {round.titleId === 'granTurismo7' && (
            <div className="form-group" style={{ maxWidth: 160 }}>
              <label className="form-label">Car Class</label>
              <select className="form-select" value={round.carClass || ''} onChange={e => set('carClass', e.target.value || null)}>
              <option value="">Any / not set</option>
              {CAR_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            </div>
          )}
          {round.titleId === 'granTurismo7' && (
            <div className="form-group" style={{ maxWidth: 170 }}>
              <label className="form-label">Tyres</label>
              <select className="form-select" value={round.tyre || ''} onChange={e => set('tyre', e.target.value || null)}>
                <option value="">Any / not set</option>
                {TYRES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div className="form-group" style={{ maxWidth: 100 }}>
            <label className="form-label">Sort Order</label>
            <input className="form-input" type="number" value={round.sortOrder} onChange={e => set('sortOrder', e.target.value)} placeholder="1, 2…" />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Race Variables</label>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {variables.map(v => (
                <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={(round.variables || []).includes(v.id)}
                    onChange={() => toggleVariable(v.id)} />
                  {v.label}
                </label>
              ))}
              {variables.length === 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No variables for this title</span>
              )}
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating...' : 'Add Round'}
          </button>
        </div>
      </div>
    </div>
  );
}
